import React, { ReactNode, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { IconButton } from '@components/button/Button';

interface ProgressItem {
  id: string;
  status: string;
  timestamp?: number;
}

interface ProgressPanelProps<T extends ProgressItem> {
  items: T[];
  title: string;
  inProgressStatus: string;
  removeAction: (id: string) => any;
  getStatusIcon: (status: string, item: T) => ReactNode;
  getStatusMessage: (item: T) => string;
  getItemIcon?: (item: T) => ReactNode;
  autoRemoveDelay?: number;
  className?: string;
}

const ProgressPanel = <T extends ProgressItem>({
  items,
  title,
  inProgressStatus,
  removeAction,
  getStatusIcon,
  getStatusMessage,
  getItemIcon,
  autoRemoveDelay = 5000,
  className = 'fixed bottom-4 right-4 z-50 w-96'
}: ProgressPanelProps<T>) => {
  const dispatch = useDispatch();

  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];

    const completedItems = items.filter(
      (item) => item.status !== inProgressStatus
    );

    completedItems.forEach((item: T) => {
      // If timestamp not present, remove immediately
      if (!item.timestamp) {
        dispatch(removeAction(item.id));
        return;
      }

      const timeSinceCompletion = Date.now() - item.timestamp;
      const timeUntilRemoval = autoRemoveDelay - timeSinceCompletion;

      if (timeUntilRemoval <= 0) {
        dispatch(removeAction(item.id));
      } else {
        const timer = setTimeout(() => {
          dispatch(removeAction(item.id));
        }, timeUntilRemoval);

        timers.push(timer);
      }
    });

    return () => {
      timers.forEach((timer) => clearTimeout(timer));
    };
  }, [items, dispatch, inProgressStatus, removeAction, autoRemoveDelay]);

  if (items.length === 0) return null;

  return (
    <div
      className={`${className} rounded-lg border border-gray-200 bg-white shadow-lg`}
    >
      <div className="flex items-center justify-between border-b border-gray-200 p-4">
        <h3 className="text-base font-medium text-gray-800">{title}</h3>
        <IconButton
          iconName="close"
          variant="transparent"
          size="medium"
          className="!size-6 !p-0"
          handleClick={() => {
            items.forEach((item) => dispatch(removeAction(item.id)));
          }}
        />
      </div>

      <div className="max-h-96 overflow-y-auto">
        {items.map((item) => (
          <div
            key={item.id}
            className="flex items-center justify-between border-b border-gray-100 p-4 last:border-b-0"
          >
            <div className="flex min-w-0 flex-1 items-center gap-[6px]">
              {getItemIcon ? getItemIcon(item) : null}
              <span className="text-sm text-gray-700">
                {getStatusMessage(item)}
              </span>
            </div>

            <div className="ml-4 flex items-center">
              {getStatusIcon(item.status, item)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProgressPanel;
